import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card/Card';
import { STORAGE_KEYS } from '../utils/constants';
import { apiHelper } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import {
  FaCalendarCheck,
  FaClock,
  FaFileInvoiceDollar,
  FaReceipt,
  FaStar,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaCalendarPlus
} from 'react-icons/fa';
import './Dashboard.scss';
import { formatDateDisplay, formatDateISO } from '../utils/date';
import { getEventAnchorDate as getSharedEventAnchor, eventOccursOnDay as sharedOccursOnDay, getActiveDatedServices, dayKeyOf, isUndatedEvent } from '../utils/eventDates';
import PlanCard from '../components/PlanCard/PlanCard';
import Analytics from '../components/Analytics';
import EventCalendar from '../components/EventCalendar/EventCalendar';

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.settings || {});
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Determine current plan
  const rawPlan = (profile && (profile.plan || profile.subscription?.tier)) || user?.plan || 'none';
  const planTier = String(rawPlan || 'none').toLowerCase();
  const isGold = planTier.includes('gold');
  const isSilver = planTier.includes('silver');

  // Mock data - Replace with actual data from API
  // Live stats derived from app data
  const projectsState = useSelector((state) => state.projects);

  // State to trigger re-reads from server/database
  const [refreshKey, setRefreshKey] = useState(0);

  // Dashboard filters
  const [dateFilter, setDateFilter] = useState('all');
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [orderTypeFilter, setOrderTypeFilter] = useState('own');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const dateFilterRef = useRef(null);

  // State for profile name fetched from backend settings
  const [profileName, setProfileName] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // State for events from backend
  const [backendEvents, setBackendEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const refreshEventsFromServer = async () => {
    try {
      setEventsLoading(true);
      const data = await apiHelper.get('/events');
      setBackendEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setBackendEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // State for quotations & billing (used to compute filtered stats)
  const [backendQuotations, setBackendQuotations] = useState([]);
  const [backendBilling, setBackendBilling] = useState([]);
  const [backendTeamMembers, setBackendTeamMembers] = useState([]);

  // Helper: check if name is valid (not empty, not dummy value)
  const isValidName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    if (!trimmed) return false;
    // Filter out common dummy/test names (case-insensitive)
    const dummyNames = ['user', 'test', 'demo', 'admin', 'guest', 'unknown', 'null', 'undefined', 'name'];
    return !dummyNames.includes(trimmed.toLowerCase());
  };

  const normalizeOrderType = (value) => {
    const normalized = String(value || 'own').toLowerCase().replace(/[\s-]/g, '');
    return normalized === 'suborder' || normalized === 'sub' ? 'suborder' : 'own';
  };

  const parseComparableDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const text = String(value).trim();
    const dateOnlyMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]) - 1;
      const day = Number(dateOnlyMatch[3]);
      const date = new Date(year, month, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const startOfDay = (value) => {
    const date = parseComparableDate(value);
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const endOfDay = (value) => {
    const date = parseComparableDate(value);
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  };

  const getRecordDate = (record) => record?.createdAt || record?.createdDate || record?.issueDate || record?.date || record?.startDate || null;

  const getRecordIdentifier = (record) => String(record?.id || record?._id || record?.quotationId || record?.quotationRef || '').trim();

  const toAmount = (value) => {
    const amount = Number.parseFloat(value);
    return Number.isNaN(amount) ? 0 : amount;
  };

  const getBillingTotalAmount = (invoice) => toAmount(invoice?.amount ?? invoice?.total ?? invoice?.totalAmount ?? 0);

  const getBillingAdditionalChargesAmount = (invoice) => {
    const charges = Array.isArray(invoice?.additionalCharges) ? invoice.additionalCharges : [];
    return charges.reduce((sum, charge) => sum + toAmount(charge?.amount), 0);
  };

  const getBillingPaidAmount = (invoice) => {
    const status = String(invoice?.status || '').toLowerCase();
    const paidValue = invoice?.paid ?? invoice?.amountPaid;

    if (paidValue !== undefined && paidValue !== null && paidValue !== '') {
      return toAmount(paidValue);
    }

    return status === 'paid' ? getBillingTotalAmount(invoice) : 0;
  };

  const getBillingPendingAmount = (invoice) => {
    const status = String(invoice?.status || '').toLowerCase();

    if (status === 'paid') {
      return 0;
    }

    return Math.max(getBillingTotalAmount(invoice) - getBillingPaidAmount(invoice), 0);
  };

  const summarizeBillingStats = (records) => {
    const list = Array.isArray(records) ? records : [];

    return list.reduce((summary, invoice) => {
      const total = getBillingTotalAmount(invoice);
      const paid = getBillingPaidAmount(invoice);
      const pending = getBillingPendingAmount(invoice);
      const additionalCharges = getBillingAdditionalChargesAmount(invoice);

      summary.total += total;
      summary.paid += paid;
      summary.pending += pending;
      summary.additionalCharges += additionalCharges;
      return summary;
    }, {
      total: 0,
      paid: 0,
      pending: 0,
      additionalCharges: 0,
    });
  };

  const getOrderTypeBreakdown = (records) => {
    return (Array.isArray(records) ? records : []).reduce((summary, record) => {
      const orderType = normalizeOrderType(record?.orderType);
      summary[orderType] += 1;
      return summary;
    }, {
      own: 0,
      suborder: 0,
    });
  };

  const getOrderTypeSubtitle = (breakdown) => {
    if (orderTypeFilter === 'own') return t('dashboard.orderOwn');
    if (orderTypeFilter === 'suborder') return t('dashboard.orderSub');
    return `${t('dashboard.orderOwn')} / ${t('dashboard.orderSub')}`;
  };

  const getTeamPaymentTotal = (eventsList, teamMembers) => {
    if (!Array.isArray(eventsList) || !Array.isArray(teamMembers) || teamMembers.length === 0) {
      return 0;
    }

    const eventIds = new Set();
    const eventNames = new Set();

    eventsList.forEach((event) => {
      const eventId = getRecordIdentifier(event);
      if (eventId) eventIds.add(eventId);

      const eventName = String(event?.clientName || event?.name || event?.quotationRef || event?.quotationId || '').trim().toLowerCase();
      if (eventName) eventNames.add(eventName);

      if (eventId) {
        eventNames.add(`event ${eventId.toLowerCase()}`);
      }
    });

    return teamMembers.reduce((total, member) => {
      const payments = Array.isArray(member?.payments) ? member.payments : [];

      return total + payments.reduce((memberTotal, payment) => {
        const paymentEventId = String(payment?.eventId || '').trim();
        const paymentEventName = String(payment?.eventName || '').trim().toLowerCase();
        const matchesEvent = paymentEventId && eventIds.has(paymentEventId);
        const matchesEventName = !matchesEvent && paymentEventName && eventNames.has(paymentEventName);

        if (!matchesEvent && !matchesEventName) {
          return memberTotal;
        }

        return memberTotal + toAmount(payment?.amount);
      }, 0);
    }, 0);
  };

  const getEventAnchorDate = (event) => {
    // Shared rule: undated events (no usable service/event date) have NO
    // anchor and never fall back to createdAt — they stay TBA.
    if (!event) return null;
    return getSharedEventAnchor(event);
  };

  const rangeBounds = useMemo(() => {
    const now = new Date();

    if (dateFilter === 'week') {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { start: startOfDay(start), end: endOfDay(now) };
    }

    if (dateFilter === 'month') {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      return { start: startOfDay(start), end: endOfDay(now) };
    }

    if (dateFilter === 'year') {
      const start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      return { start: startOfDay(start), end: endOfDay(now) };
    }

    if (dateFilter === 'custom') {
      const from = startOfDay(customDateRange.from);
      const to = endOfDay(customDateRange.to);

      if (from && to && from > to) {
        return { start: to, end: from };
      }

      return { start: from, end: to };
    }

    return { start: null, end: null };
  }, [dateFilter, customDateRange.from, customDateRange.to]);

  const isInRange = (value) => {
    const date = parseComparableDate(value);
    if (!date) return false;

    if (!rangeBounds.start && !rangeBounds.end) {
      return true;
    }

    if (rangeBounds.start && date < rangeBounds.start) {
      return false;
    }

    if (rangeBounds.end && date > rangeBounds.end) {
      return false;
    }

    return true;
  };

  const matchesOrderType = (record) => {
    if (orderTypeFilter === 'all') {
      return true;
    }

    return normalizeOrderType(record?.orderType) === orderTypeFilter;
  };

  useEffect(() => {
    if (dateFilter !== 'custom') return;

    setCustomDateRange((previous) => {
      if (previous.from || previous.to) return previous;

      const today = new Date();
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: formatDateISO(firstOfMonth),
        to: formatDateISO(today),
      };
    });
  }, [dateFilter]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dateFilterRef.current) return;
      if (!dateFilterRef.current.contains(event.target)) {
        setIsDateFilterOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDateFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Display name: prefer Redux settings profile (Navbar's loadSettings already
  // fetched /settings) and only hit the network if it's missing — avoids a
  // duplicate GET /settings on every Dashboard mount.
  const reduxProfileName = profile?.fullName || profile?.name || null;
  useEffect(() => {
    if (reduxProfileName) {
      setProfileName(reduxProfileName);
      return;
    }
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        setProfileLoading(true);
        const data = await apiHelper.get('/settings');
        // settings controller returns a settings document which may include `profile`
        const name = data && data.profile && (data.profile.name || data.profile.displayName || data.profile.fullName);
        if (!cancelled && name) setProfileName(name);
      } catch (err) {
        // ignore - we'll fall back to Redux `user` value
        if (!cancelled) console.error('[Dashboard] Failed to load profile from settings:', err);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    fetchProfile();
    return () => { cancelled = true; };
  }, [reduxProfileName]);

  // Fetch events from backend
  useEffect(() => {
    refreshEventsFromServer();
  }, []);

  useEffect(() => {
    const handleEventsChanged = () => {
      refreshEventsFromServer();
    };

    window.addEventListener('photoflow:events-changed', handleEventsChanged);
    return () => window.removeEventListener('photoflow:events-changed', handleEventsChanged);
  }, []);

  // Fetch quotations + billing once (we'll compute date-filtered stats client-side)
  useEffect(() => {
    const fetchBillingAndQuotations = async () => {
      try {
        const [qRes, bRes, tRes] = await Promise.all([
          apiHelper.get('/quotations').catch(() => []),
          apiHelper.get('/billing').catch(() => []),
          apiHelper.get('/team').catch(() => []),
        ]);
        setBackendQuotations(Array.isArray(qRes) ? qRes : []);
        setBackendBilling(Array.isArray(bRes) ? bRes : []);
        setBackendTeamMembers(Array.isArray(tRes) ? tRes : []);
      } catch (err) {
        console.error('[Dashboard] Failed to load quotations/billing:', err);
        setBackendQuotations([]);
        setBackendBilling([]);
        setBackendTeamMembers([]);
      }
    };

    fetchBillingAndQuotations();
  }, []);

  // Re-read from server/database when component mounts or becomes visible.
  // NOTE: this must actually refetch events — the previous version only
  // bumped `refreshKey` (which nothing event-related reads), leaving the
  // calendar showing stale days after an edit on the Events page.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshEventsFromServer();
      }
    };

    const handleFocus = () => {
      refreshEventsFromServer();
    };

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Read invoices from database (will be fetched via API)
  const invoices = useMemo(() => {
    // Data fetched from API via other components
    return [];
  }, [refreshKey]);

  // Read clients from database (will be fetched via API)
  const clients = useMemo(() => {
    // Data fetched from API via other components
    return [];
  }, [refreshKey]);

  // Read quotations from database (will be fetched via API)
  const quotations = useMemo(() => {
    // Data fetched from API via other components
    return [];
  }, [refreshKey]);

  // Process events from backend (filter out completed/cancelled)
  const events = useMemo(() => {
    try {
      const all = backendEvents;
      if (!Array.isArray(all)) {
        console.log('[Dashboard] Backend events is not an array');
        return [];
      }

      // Helper: derive an event-level status from top-level status and individual services
      const deriveEventStatus = (ev) => {
        if (!ev) return '';
        const top = String(ev.eventStatus || ev.status || '').toLowerCase();
        if (top === 'completed' || top === 'cancelled' || top === 'canceled') return top;

        const services = Array.isArray(ev.services) ? ev.services : [];
        if (services.length === 0) return top || '';

        // collect normalized statuses from services
        const svcStatuses = services.map(s => String((s && (s.status || s.serviceStatus)) || '').toLowerCase()).filter(Boolean);
        if (svcStatuses.length === 0) return top || '';

        const allCompleted = svcStatuses.every(st => st === 'completed');
        if (allCompleted) return 'completed';

        const allCancelled = svcStatuses.every(st => st === 'cancelled' || st === 'canceled');
        if (allCancelled) return 'cancelled';

        // Mixed or other statuses -> return top if present, else 'scheduled'
        return top || 'scheduled';
      };

      // Exclude events whose derived status is completed/cancelled
      const filtered = all.filter(ev => {
        const derived = deriveEventStatus(ev);
        return derived !== 'completed' && derived !== 'cancelled' && derived !== 'canceled';
      });

      console.log('[Dashboard] Total events from backend:', all.length);
      console.log('[Dashboard] Filtered active events:', filtered.length);
      return filtered.map((event) => ({
        ...event,
        orderType: normalizeOrderType(event?.orderType),
      }));
    } catch (e) {
      console.error('[Dashboard] Error processing events:', e);
      return [];
    }
  }, [backendEvents]);

  // Filtered lists based on the selected dashboard filters
  const eventsInDateRange = useMemo(() => {
    if (!Array.isArray(backendEvents)) return [];
    return backendEvents.filter((ev) => isInRange(getRecordDate(ev)));
  }, [backendEvents, dateFilter, customDateRange.from, customDateRange.to]);

  const billingInDateRange = useMemo(() => {
    if (!Array.isArray(backendBilling)) return [];
    return backendBilling.filter((bill) => isInRange(getRecordDate(bill)));
  }, [backendBilling, dateFilter, customDateRange.from, customDateRange.to]);

  const filteredEventsForStats = useMemo(() => {
    return eventsInDateRange.filter((ev) => matchesOrderType(ev));
  }, [eventsInDateRange, orderTypeFilter]);

  const filteredQuotationsForStats = useMemo(() => {
    if (!Array.isArray(backendQuotations)) return [];
    return backendQuotations.filter((quotation) => matchesOrderType(quotation) && isInRange(getRecordDate(quotation)));
  }, [backendQuotations, dateFilter, orderTypeFilter, customDateRange.from, customDateRange.to]);

  const filteredBillingForStats = useMemo(() => {
    return billingInDateRange.filter((bill) => matchesOrderType(bill));
  }, [billingInDateRange, orderTypeFilter]);

  const eventOrderBreakdown = useMemo(() => getOrderTypeBreakdown(eventsInDateRange), [eventsInDateRange]);
  const billingOrderBreakdown = useMemo(() => getOrderTypeBreakdown(billingInDateRange), [billingInDateRange]);

  const filteredBillingStats = useMemo(() => summarizeBillingStats(filteredBillingForStats), [filteredBillingForStats]);
  const filteredTeamPaymentTotal = useMemo(
    () => getTeamPaymentTotal(filteredEventsForStats, backendTeamMembers),
    [backendTeamMembers, filteredEventsForStats],
  );
  const totalTeamPaymentAll = useMemo(() => {
    if (!Array.isArray(backendTeamMembers)) return 0;
    return backendTeamMembers.reduce((total, member) => {
      const payments = Array.isArray(member?.payments) ? member.payments : [];
      return total + payments.reduce((sum, p) => sum + toAmount(p?.amount), 0);
    }, 0);
  }, [backendTeamMembers]);

  const stats = useMemo(() => {
    const inrFormatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
    const totalEvents = filteredEventsForStats.length;
    const totalBills = filteredBillingForStats.length;
    const profitAmount = Math.max(
      filteredBillingStats.total - filteredBillingStats.additionalCharges - totalTeamPaymentAll,
      0,
    );

    return [
      {
        title: t('dashboard.statTotalEvents'),
        value: String(totalEvents),
        icon: <FaCalendarCheck />,
        color: '#ec4899',
        subtitle: getOrderTypeSubtitle(eventOrderBreakdown),
      },
      {
        title: t('dashboard.statTotalBills'),
        value: String(totalBills),
        icon: <FaReceipt />,
        color: '#8b5cf6',
        subtitle: getOrderTypeSubtitle(billingOrderBreakdown),
      },
      {
        title: t('dashboard.statTotal'),
        value: inrFormatter.format(filteredBillingStats.total),
        icon: <FaFileInvoiceDollar />,
        color: '#f59e0b',
        subtitle: t('dashboard.statGross'),
      },
      {
        title: t('dashboard.statPending'),
        value: inrFormatter.format(filteredBillingStats.pending),
        icon: <FaClock />,
        color: '#f97316',
        subtitle: t('dashboard.statOutstanding'),
      },
      {
        title: t('dashboard.statPaid'),
        value: inrFormatter.format(filteredBillingStats.paid),
        icon: <FaCheck />,
        color: '#10b981',
        subtitle: t('dashboard.statCollected'),
      },
      {
        title: t('dashboard.statProfit'),
        value: inrFormatter.format(profitAmount),
        icon: <FaStar />,
        color: '#14b8a6',
        subtitle: t('dashboard.statProfitSub'),
      },
    ];

  }, [billingOrderBreakdown.own, billingOrderBreakdown.suborder, eventOrderBreakdown.own, eventOrderBreakdown.suborder, filteredBillingStats, filteredBillingForStats.length, filteredEventsForStats.length, filteredTeamPaymentTotal, orderTypeFilter, t]);

  const visibleEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    // Dated events must fall in range; undated (TBA) events are kept when no
    // date filter is active so the list/calendar stay in sync about them.
    return events.filter((event) => {
      if (isUndatedEvent(event)) return !rangeBounds.start && !rangeBounds.end;
      return isInRange(getEventAnchorDate(event));
    });
  }, [events, dateFilter, customDateRange.from, customDateRange.to]);

  const recentEvents = useMemo(() => {
    console.log('[Dashboard] Computing recentEvents, total events:', visibleEvents.length);
    try {
      if (!Array.isArray(visibleEvents)) {
        console.log('[Dashboard] Events is not an array');
        return [];
      }

      if (visibleEvents.length === 0) {
        console.log('[Dashboard] No events available');
        return [];
      }

      // Get earliest occurrence for an event (null when undated → TBA last).
      const getEarliest = (ev) => getSharedEventAnchor(ev);

      const withEarliest = visibleEvents.map(ev => ({ ev, earliest: getEarliest(ev) }));

      // Today's midnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      console.log('[Dashboard] Today:', today);

      // Upcoming events: earliest date >= today, sorted ascending by earliest
      const upcoming = withEarliest
        .filter(i => {
          const hasDate = i.earliest && i.earliest >= today;
          if (i.earliest) {
            console.log(`[Dashboard] Event "${i.ev.clientName || i.ev.name}" date: ${i.earliest}, is upcoming: ${hasDate}`);
          }
          return hasDate;
        })
        .sort((a, b) => a.earliest - b.earliest)
        .map(i => i.ev);

      console.log('[Dashboard] Found upcoming events:', upcoming.length);

      let source = [];
      if (upcoming.length > 0) {
        source = upcoming;
      } else {
        console.log('[Dashboard] No upcoming events, showing recent past events');
        // No upcoming: show past events sorted by latest date (most recent first)
        const past = withEarliest
          .filter(i => i.earliest)
          .sort((a, b) => b.earliest - a.earliest)
          .map(i => i.ev);
        source = past.length > 0 ? past : visibleEvents.filter((ev) => getSharedEventAnchor(ev));
      }

      // Undated (TBA) events can never be ordered by date — append them last
      // so they stay visible instead of vanishing from the list.
      const undated = visibleEvents.filter((ev) => !getSharedEventAnchor(ev));
      const datedIds = new Set(source.map((ev) => ev?.id || ev?._id));
      undated.forEach((ev) => {
        const id = ev?.id || ev?._id;
        if (!id || !datedIds.has(id)) source.push(ev);
      });

      const result = source.slice(0, 10);
      console.log('[Dashboard] Returning events:', result.length);
      return result;
    } catch (e) {
      console.error('[Dashboard] Error computing recentEvents:', e);
      return [];
    }
  }, [visibleEvents]);

  // Selected calendar day (midnight timestamp) — clicking a calendar day filters
  // the upcoming list to that day. Null = no filter, show upcoming.
  const [selectedDayKey, setSelectedDayKey] = useState(null);

  // Ref to the calendar for the header navigation buttons (prev / today / next).
  const calendarRef = useRef(null);

  // Calendar visual defaults to today pre-selected; the list still shows
  // upcoming events until the user picks a day.
  const todayKey = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  }, []);

  const handleDaySelect = (date) => {
    if (!date) return;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return;
    const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    setSelectedDayKey((prev) => (prev === key ? null : key));
  };

  const clearDayFilter = () => setSelectedDayKey(null);

  const openEvent = (ev) => {
    if (ev && (ev.id || ev._id)) {
      navigate('/events', { state: { openViewId: ev.id || ev._id } });
    } else {
      navigate('/events');
    }
  };

  const getCountdown = (ev) => {
    try {
      const svcDates = (ev?.services || [])
        .map((s) => (s && s.date ? new Date(s.date) : null))
        .filter((d) => d && !Number.isNaN(d.getTime()));
      const anchor = svcDates.length
        ? svcDates.reduce((a, b) => (a < b ? a : b))
        : ev?.date
          ? new Date(ev.date)
          : null;
      if (!anchor || Number.isNaN(anchor.getTime())) return null;
      const a = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
      const now = new Date();
      const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffDays = Math.round((a - t0) / 86400000);
      if (diffDays < 0) return { label: t('dashboard.overdue'), tone: 'overdue' };
      if (diffDays === 0) return { label: t('dashboard.today'), tone: 'today' };
      if (diffDays === 1) return { label: t('dashboard.tomorrow'), tone: 'soon' };
      if (diffDays <= 7) return { label: t('dashboard.inDays').replace('{count}', String(diffDays)), tone: 'soon' };
      return { label: t('dashboard.inDays').replace('{count}', String(diffDays)), tone: 'later' };
    } catch {
      return null;
    }
  };

  const getEventLocation = (ev) => ev?.venue || ev?.location || ev?.address || ev?.city || '';

  const getActiveServicePairs = (ev) => {
    // Same occurrence rule as the calendar: active dated services only,
    // with date-only strings pinned to local midnight.
    return getActiveDatedServices(ev)
      .map((s) => {
        const key = dayKeyOf(s.date);
        return key === null
          ? null
          : { name: s.name, rawDate: s.date, dateObj: new Date(key) };
      })
      .filter(Boolean)
      .sort((a, b) => a.dateObj - b.dateObj);
  };

  const getEventAnchor = (ev) => getSharedEventAnchor(ev);

  const eventOccursOnDay = (ev, dayKey) => sharedOccursOnDay(ev, dayKey);

  const dayEvents = useMemo(() => {
    if (!selectedDayKey || !Array.isArray(visibleEvents)) return [];
    return visibleEvents.filter((ev) => eventOccursOnDay(ev, selectedDayKey));
  }, [visibleEvents, selectedDayKey]);

  const listEvents = selectedDayKey ? dayEvents : recentEvents;

  const selectedDayLabel = useMemo(() => {
    if (!selectedDayKey) return '';
    return new Date(selectedDayKey).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  }, [selectedDayKey]);

  // Optional: force re-evaluation on resize so recentEvents picks correct slice

  return (
    <div className="dashboard">
      <div className="dashboard__header">
        <div className="dashboard__headline">
          <h1 className="dashboard__title">{t('dashboard.welcomeBack')}, {(isValidName(profileName) && profileName) || 'Unknown'}!</h1>
          <p className="dashboard__subtitle">{t('dashboard.todaySummary')}</p>
        </div>

        <div className="dashboard__filters" role="group" aria-label="Dashboard filters">
          <div className="dashboard__select" ref={dateFilterRef}>
            <span className="sr-only">Date range</span>
            <button
              type="button"
              className="dashboard__select-button"
              onClick={() => setIsDateFilterOpen((previous) => !previous)}
              aria-haspopup="listbox"
              aria-expanded={isDateFilterOpen}
              aria-label="Date range filter"
            >
              <span className="dashboard__select-value">
                {dateFilter === 'all' ? t('dashboard.filterAll') : dateFilter === 'week' ? t('dashboard.filterWeek') : dateFilter === 'month' ? t('dashboard.filterMonth') : dateFilter === 'year' ? t('dashboard.filterYear') : t('dashboard.filterCustom')}
              </span>
              <FaChevronDown aria-hidden="true" />
            </button>

            {isDateFilterOpen && (
              <div className="dashboard__select-menu" role="listbox" aria-label="Date range options">
                {[
                  { value: 'all', label: t('dashboard.filterAll') },
                  { value: 'week', label: t('dashboard.filterWeek') },
                  { value: 'month', label: t('dashboard.filterMonth') },
                  { value: 'year', label: t('dashboard.filterYear') },
                  { value: 'custom', label: t('dashboard.filterCustom') },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`dashboard__select-option${dateFilter === option.value ? ' dashboard__select-option--active' : ''}`}
                    onClick={() => {
                      setDateFilter(option.value);
                      setIsDateFilterOpen(false);
                    }}
                    role="option"
                    aria-selected={dateFilter === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {dateFilter === 'custom' && (
            <div className="dashboard__custom-range" aria-label="Custom date range">
              <label className="dashboard__date-field">
                <span>{t('dashboard.filterFrom')}</span>
                <input
                  type="date"
                  value={customDateRange.from}
                  onChange={(e) => setCustomDateRange((previous) => ({ ...previous, from: e.target.value }))}
                  aria-label="From date"
                />
              </label>
              <label className="dashboard__date-field">
                <span>{t('dashboard.filterTo')}</span>
                <input
                  type="date"
                  value={customDateRange.to}
                  onChange={(e) => setCustomDateRange((previous) => ({ ...previous, to: e.target.value }))}
                  aria-label="To date"
                />
              </label>
            </div>
          )}

          <div className="dashboard__order-filter" aria-label="Order type filter">
            <span className="dashboard__order-label dashboard__order-label--own">{t('dashboard.orderOwn')}</span>
            <label className="dashboard__switch">
              <input
                type="checkbox"
                checked={orderTypeFilter === 'suborder'}
                onChange={(e) => setOrderTypeFilter(e.target.checked ? 'suborder' : 'own')}
                aria-label="Toggle sub order filter"
              />
              <span className="dashboard__switch-track" aria-hidden="true">
                <span className="dashboard__switch-thumb" />
              </span>
            </label>
            <span className="dashboard__order-label dashboard__order-label--sub">{t('dashboard.orderSub')}</span>
          </div>
        </div>
      </div>
    
      <div className="dashboard__stats">
        {stats.map((stat, index) => (
          <Card
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            subtitle={stat.subtitle}
          />
        ))}
      </div>

      <div className="dashboard__content dashboard__content--split">
        <div className="dashboard__section dashboard__section--calendar">
          <div className="section-header">
            <h2 className="section-title">{t('dashboard.eventCalendar')}</h2>
            <div className="evcal__nav" role="group" aria-label="Calendar navigation">
              <button
                type="button"
                className="evcal__nav-btn"
                onClick={() => calendarRef.current?.goMonth(-1)}
                aria-label="Previous month"
              >
                <FaChevronLeft aria-hidden="true" />
              </button>
              <button
                type="button"
                className="evcal__today-btn"
                onClick={() => calendarRef.current?.goToday()}
              >
                {t('dashboard.today')}
              </button>
              <button
                type="button"
                className="evcal__nav-btn"
                onClick={() => calendarRef.current?.goMonth(1)}
                aria-label="Next month"
              >
                <FaChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>

          {eventsLoading ? (
            <div className="events-skeleton" aria-label="Loading events">
              {[0, 1, 2].map((i) => (
                <div key={i} className="events-skeleton__row" />
              ))}
            </div>
          ) : (
            <EventCalendar
              ref={calendarRef}
              events={visibleEvents}
              selectedKey={selectedDayKey ?? todayKey}
              onDaySelect={handleDaySelect}
            />
          )}
          <p className="calendar-hint">{t('dashboard.calendarHint')}</p>
        </div>

        <div className="dashboard__section dashboard__section--upcoming">
          <div className="section-header">
            <h2 className="section-title">
              {selectedDayKey ? selectedDayLabel : t('dashboard.upcomingEvents')}
              {listEvents.length > 0 && (
                <span className="section-count">{listEvents.length}</span>
              )}
            </h2>
            <div className="upcoming-header-actions">
              {selectedDayKey && (
                <button type="button" className="btn-link" onClick={clearDayFilter}>
                  {t('dashboard.showUpcoming')}
                </button>
              )}
              <button className="btn-link" onClick={() => navigate('/events')}>{t('common.viewAll')}</button>
            </div>
          </div>

          {eventsLoading ? (
            <div className="events-skeleton" aria-label="Loading events">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="events-skeleton__row" />
              ))}
            </div>
          ) : (
          <ul className="upcoming-timeline">
            {listEvents.length ? listEvents.map((ev, idx) => {
              const clientName = ev.clientName || ev.title || ev.name || 'Untitled Event';
              const pairs = getActiveServicePairs(ev);
              const dates = pairs.length
                ? Array.from(new Set(pairs.map((p) => p.rawDate)))
                : ev.date
                  ? [ev.date]
                  : [];
              const dateDisplay = !dates.length
                ? t('dashboard.dateTBA')
                : dates.map((d) => formatDateDisplay(d)).join('  ·  ');
              const serviceNames = pairs.map((p) => p.name).filter(Boolean);
              const servicesLine = serviceNames.join('  ·  ');
              const countdown = getCountdown(ev);
              const location = getEventLocation(ev);
              const orderType = normalizeOrderType(ev?.orderType);
              const anchor = getEventAnchor(ev);
              const metaLine = [dateDisplay, location].filter(Boolean).join('   ·   ');

              return (
                <li
                  key={ev?.id || ev?._id || idx}
                  className={`upcoming-card upcoming-card--${countdown?.tone || 'later'}`}
                >
                  <button
                    type="button"
                    className="upcoming-card__body"
                    onClick={() => openEvent(ev)}
                    aria-label={`${clientName}, ${dateDisplay}`}
                  >
                    <span className="upcoming-card__accent" aria-hidden="true" />
                    {anchor && (
                      <span className="upcoming-card__date" aria-hidden="true">
                        <span className="upcoming-card__day">{String(anchor.getDate()).padStart(2, '0')}</span>
                        <span className="upcoming-card__mon">
                          {anchor.toLocaleDateString('en-IN', { month: 'short' })}
                        </span>
                      </span>
                    )}
                    <span className="upcoming-card__content">
                      <span className="upcoming-card__row">
                        <span className="upcoming-card__name">{clientName}</span>
                        <span className={`upcoming-card__order upcoming-card__order--${orderType}`}>
                          {orderType === 'suborder' ? t('dashboard.orderSub') : t('dashboard.orderOwn')}
                        </span>
                        {countdown && (
                          <span className={`upcoming-card__when upcoming-card__when--${countdown.tone}`}>
                            {countdown.label}
                          </span>
                        )}
                      </span>
                      {servicesLine && (
                        <span className="upcoming-card__services" title={servicesLine}>
                          {servicesLine}
                        </span>
                      )}
                      <span className="upcoming-card__meta">{metaLine}</span>
                    </span>
                    <FaChevronRight className="upcoming-card__chev" aria-hidden="true" />
                  </button>
                </li>
              );
            }) : selectedDayKey ? (
              <li className="upcoming-empty">
                <div className="upcoming-empty__icon" aria-hidden="true">
                  <FaCalendarPlus />
                </div>
                <h3>{t('dashboard.noEventsOnDay')}</h3>
                <p>{t('dashboard.noEventsOnDayHint')}</p>
                <button
                  type="button"
                  className="upcoming-empty__cta"
                  onClick={clearDayFilter}
                >
                  {t('dashboard.showUpcoming')}
                </button>
              </li>
            ) : (
              <li className="upcoming-empty">
                <div className="upcoming-empty__icon" aria-hidden="true">
                  <FaCalendarPlus />
                </div>
                <h3>{t('dashboard.noUpcomingEvents')}</h3>
                <p>{t('dashboard.noUpcomingHint')}</p>
                <button
                  type="button"
                  className="upcoming-empty__cta"
                  onClick={() => navigate('/events', { state: { openCreate: true } })}
                >
                  {t('dashboard.createEvent')}
                </button>
              </li>
            )}
          </ul>
          )}
        </div>
      </div>

      <Analytics orderType={orderTypeFilter} />

      <div className="dashboard__plans">
        <div className="plan-modal__options">
          <PlanCard tier="Silver" isCurrentPlan={isSilver} />

          <PlanCard tier="Gold" highlighted isCurrentPlan={isGold} />
        </div>
      </div>
    </div>
  );
};


export default Dashboard;
